import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Waves, MapPin, Settings, X, RefreshCw } from 'lucide-react'
import { windTideAPI, widgetAPI } from '../../services/api'
import { getPreferredZipCode, savePreferredZipCode } from '../../utils/zipCodePreference'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface TideWidgetProps {
  widget: any
  isExpanded: boolean
}

export default function TideWidget({ widget, isExpanded }: TideWidgetProps) {
  const [zipCode, setZipCode] = useState(widget.config?.zipCode || getPreferredZipCode())
  const [tempZipCode, setTempZipCode] = useState(zipCode)
  const [showSettings, setShowSettings] = useState(false)
  const queryClient = useQueryClient()

  // Fetch tide data
  const { data: tideData, isLoading, refetch } = useQuery({
    queryKey: ['tide-data', zipCode],
    queryFn: async () => {
      const { data } = await windTideAPI.getData(zipCode)
      return data
    },
    enabled: !!zipCode,
  })

  // Update widget config mutation
  const updateWidgetMutation = useMutation({
    mutationFn: async (newZipCode: string) => {
      const newConfig = {
        ...widget.config,
        zipCode: newZipCode,
      }
      const { data } = await widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig),
      })
      return data
    },
    onSuccess: () => {
      toast.success('ZIP code saved!')
      queryClient.invalidateQueries({ queryKey: ['widgets'] })
    },
    onError: () => {
      toast.error('Failed to save ZIP code')
    },
  })

  const handleUpdateZipCode = async () => {
    setZipCode(tempZipCode)
    setShowSettings(false)
    
    // Save to shared preference for other widgets
    savePreferredZipCode(tempZipCode)
    
    await updateWidgetMutation.mutateAsync(tempZipCode)
  }

  if (showSettings) {
    return (
      <div className="h-full flex flex-col p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Settings</h3>
          <button onClick={() => setShowSettings(false)} className="p-1 rounded hover:opacity-70" style={{ color: 'var(--color-text-primary)' }}>
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              ZIP Code (Coastal Areas Only)
            </label>
            <input
              type="text"
              value={tempZipCode}
              onChange={(e) => setTempZipCode(e.target.value)}
              placeholder="Enter ZIP code"
              className="w-full px-3 py-2 border rounded-lg"
              style={{ 
                backgroundColor: 'var(--color-surface)', 
                borderColor: 'var(--color-border)', 
                color: 'var(--color-text-primary)' 
              }}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Popular Coastal ZIP Codes:</strong>
            </p>
            <ul className="text-xs text-blue-700 mt-2 space-y-1">
              <li>• 33139 - Miami Beach, FL</li>
              <li>• 02134 - Boston, MA</li>
              <li>• 94102 - San Francisco, CA</li>
              <li>• 92037 - La Jolla, CA</li>
              <li>• 10001 - New York, NY</li>
              <li>• 98101 - Seattle, WA</li>
            </ul>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleUpdateZipCode}
            className="flex-1 px-4 py-2 text-white rounded-lg transition"
            style={{ backgroundColor: 'var(--color-button-bg, #0183c6)' }}
          >
            Update
          </button>
          <button
            onClick={() => setShowSettings(false)}
            className="px-4 py-2 rounded-lg transition hover:opacity-80"
            style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-primary)' }}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="animate-spin text-gray-400" size={32} />
      </div>
    )
  }

  if (!tideData) {
    return (
      <div className="h-full flex items-center justify-center p-4 text-center">
        <div>
          <Waves size={48} className="mx-auto mb-2 text-gray-300" />
          <p className="text-gray-600 mb-3">No data available</p>
          <button
            onClick={() => setShowSettings(true)}
            className="px-4 py-2 text-white rounded-lg transition"
            style={{ backgroundColor: 'var(--color-button-bg, #0183c6)' }}
          >
            Set ZIP Code
          </button>
        </div>
      </div>
    )
  }

  // Get current/next tide and calculate position
  const getCurrentTideInfo = () => {
    if (!tideData.tide_data || tideData.tide_data.length === 0) return null
    
    const now = new Date()
    const tides = tideData.tide_data.map((t: any) => ({
      ...t,
      time: new Date(t.time)
    }))
    
    // Find next tide
    const nextTide = tides.find((t: any) => t.time > now)
    const nextIndex = tides.indexOf(nextTide)
    
    // Find previous tide
    const previousTide = nextIndex > 0 ? tides[nextIndex - 1] : null
    
    // Calculate position between tides (0-100%)
    let position = 50
    let isRising = false
    if (previousTide && nextTide) {
      const totalTime = nextTide.time.getTime() - previousTide.time.getTime()
      const elapsedTime = now.getTime() - previousTide.time.getTime()
      position = Math.max(0, Math.min(100, (elapsedTime / totalTime) * 100))
      isRising = nextTide.type === 'high'
    }
    
    return {
      current: nextTide || tides[0],
      previous: previousTide,
      next: nextTide,
      position,
      isRising
    }
  }

  const tideInfo = getCurrentTideInfo()

  // Render tide meter
  const renderTideMeter = () => {
    if (!tideInfo) return null
    
    // Calculate fill percentage: full at high tide, empty at low tide
    const fillPercentage = tideInfo.isRising 
      ? tideInfo.position  // Rising: 0% at low → 100% at high
      : 100 - tideInfo.position  // Falling: 100% at high → 0% at low
    
    return (
      <div className="mb-4">
        {/* Tide Meter */}
        <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
          {/* Fill showing tide position */}
          <div 
            className="absolute top-0 left-0 h-full transition-all duration-500"
            style={{ 
              width: `${fillPercentage}%`,
              backgroundColor: 'var(--color-primary)'
            }}
          />
          {/* Indicator */}
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-1 h-6 shadow-lg transition-all duration-500"
            style={{ 
              left: `${fillPercentage}%`,
              backgroundColor: 'var(--color-text-primary)'
            }}
          />
        </div>
        
        {/* Labels */}
        <div className="flex items-center justify-between mt-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          <span className="flex items-center gap-1">
            🏖️ Low (0%)
          </span>
          <span className="font-medium" style={{ color: 'var(--color-primary)' }}>
            {tideInfo.isRising ? 'Rising ↗' : 'Falling ↘'}
          </span>
          <span className="flex items-center gap-1">
            🌊 High (100%)
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-widget-bg)' }}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MapPin size={14} className="flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>
              {tideData.location_name}
            </div>
            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{zipCode}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => refetch()}
            className="p-1.5 rounded transition hover:opacity-70"
            style={{ color: 'var(--color-text-primary)' }}
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="p-1.5 rounded transition hover:opacity-70"
            style={{ color: 'var(--color-text-primary)' }}
            title="Settings"
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Tide Information */}
        {tideData.tide_data && tideData.tide_data.length > 0 && tideData.is_coastal ? (
          <div>
            {/* Tide Meter - Always Show */}
            {renderTideMeter()}
            
            {/* Current/Next Tide - Compact View */}
            {!isExpanded && tideInfo && (
              <div 
                className="rounded-lg p-3 border-2"
                style={{ 
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">
                      {tideInfo.current.type === 'high' ? '🌊 Next High Tide' : '🏖️ Next Low Tide'}
                    </div>
                    <div className="text-xs mt-1 opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
                      {format(tideInfo.current.time, 'h:mm a')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">
                      {tideInfo.current.height}
                    </div>
                    <div className="text-xs opacity-70" style={{ color: 'var(--color-text-secondary)' }}>feet</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Full Tide List - Expanded View Only */}
            {isExpanded && (
              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Waves size={18} style={{ color: 'var(--color-primary)' }} />
                  <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Upcoming Tides</h3>
                  {tideData.tide_station && (
                    <div className="text-xs ml-auto" style={{ color: 'var(--color-text-secondary)' }}>
                      Station: {tideData.tide_station}
                    </div>
                  )}
                </div>
                
                {tideData.tide_data.map((tide: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--color-surface)',
                      borderColor: 'var(--color-primary)'
                    }}
                  >
                    <div>
                      <div className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {tide.type === 'high' ? '🌊 High Tide' : '🏖️ Low Tide'}
                      </div>
                      <div className="text-xs mt-1 opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
                        {format(new Date(tide.time), 'EEEE, MMM d • h:mm a')}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                        {tide.height}
                      </div>
                      <div className="text-xs opacity-70" style={{ color: 'var(--color-text-secondary)' }}>feet</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border-2 p-6 text-center" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <Waves className="mx-auto mb-3" style={{ color: 'var(--color-text-secondary)' }} size={48} />
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              {tideData.is_coastal === false
                ? 'Tide Data Not Available'
                : 'No Tide Data'}
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {tideData.is_coastal === false
                ? 'This location is too far inland. Tide data is only available for coastal areas within ~100 miles of a NOAA tide station.'
                : 'Tide data is not available for this location.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

